import LoadingButton from '@/common/components/base/button/loadingButton';
import DateInput from '@/common/components/base/input/dateInput/DateInput';
import ModalInput from '@/common/components/base/input/modalInput/ModalInput';
import RadioGroup from '@/common/components/base/input/radioGroup/RadioGroup';
import TextInput from '@/common/components/base/input/textInput/TextInput';
import {
  ONLY_NUMBERS_RULE,
  PERSIAN_LATTER_RULE,
} from '@/common/components/base/input/textInput/textInput.rules';
import { atFaultIdentifierQueryOptions } from '@/common/queries/selections/atFaultIdentifier/atFaultIdentifierQueryOptions';
import { bodyAtFaultQueryOptions } from '@/common/queries/selections/bodyAtFault/bodyAtFaultQueryOptions';
import usePersonalInquiryStore from '@/common/store/personalInquiry/usePersonalInquiry';
import { NATIONAL_CODE_RULE } from '@/common/utils/rules/nationalCode.rules';
import { requiredText } from '@/common/utils/rules/publicRules';
import { validateNotFutureDate } from '@/modules/adjusterReportModules/IncidentDetails/utils/incidentDetails.util';
import PersonalInquiry from '@/modules/adjusterReportModules/IncidentParticipants/components/common/personalInquiry/PersonalInquiry';
import { ADJUSTER_REPORT_BODY_DAMAGE_LINKS } from '@/modules/adjusterReportModules/adjusterReport/links/bodyDamage/bodyDamageLinks';
import { BodyDriverInfoTypeForm } from '@/modules/adjusterReportModules/Insurer-information/pages/driver-owner/driverInfo/BodyDriverInfo.type';
import { patchBodyPersonalDriverDataQueryOptions } from '@/modules/adjusterReportModules/Insurer-information/queries/patchBodyPersonalDriverData/patchBodyPersonalDriverDataQueryOptions';
import { useMutation, useQuery } from '@tanstack/react-query';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { css } from 'styled-system/css';
import { Box, Flex } from 'styled-system/jsx';

const BodyInitialDataDriver: React.FC = () => {
  const methods = useFormContext<BodyDriverInfoTypeForm>();
  const { id } = useParams();
  const navigate = useNavigate();
  const { clearInquiryData } = usePersonalInquiryStore();
  const { data: atFaultIdentifiers } = useQuery(
    atFaultIdentifierQueryOptions(),
  );
  const { data: atBodyFaults } = useQuery(bodyAtFaultQueryOptions());
  const { mutate: sendPersonalDriverData, isPending: loading } = useMutation({
    ...patchBodyPersonalDriverDataQueryOptions(),
    onSuccess: () => {
      navigate(
        ADJUSTER_REPORT_BODY_DAMAGE_LINKS.BODY_DAMAGE_INFORMATION({
          id: id!,
        }).DRIVER_OWNER_INFORMATION.certificate,
        { replace: true },
      );
    },
  });

  const onSubmit = (formData: BodyDriverInfoTypeForm) => {
    sendPersonalDriverData({
      data: {
        ...formData.personal_information,
        at_fault_identifier:
          +formData?.personal_information?.at_fault_identifier?.value!,
        at_fault: formData?.personal_information?.at_fault?.value!,
        father_name:
          formData?.personal_information?.father_name === ''
            ? null
            : formData?.personal_information?.father_name,
      },
      id: Number(id),
    });
    clearInquiryData();
  };
  return (
    <Flex direction="column" gap={10}>
      <Box py={24}>
        <PersonalInquiry outSideForm={useFormContext} />
      </Box>{' '}
      <Box>
        <ModalInput
          label="مرجع تعیین مقصر"
          name="personal_information.at_fault_identifier"
          placeholder="انتخاب کنید"
          rules={{ ...PERSIAN_LATTER_RULE, required: requiredText }}
          options={atFaultIdentifiers || []}
          defaultValue=""
        />
      </Box>
      <Box>
        <ModalInput
          name="personal_information.at_fault"
          label="مقصر حادثه"
          placeholder="انتخاب کنید"
          rules={{ ...PERSIAN_LATTER_RULE, required: requiredText }}
          options={atBodyFaults || []}
          defaultValue=""
        />
      </Box>
      <RadioGroup
        title="جنسیت راننده"
        name="personal_information.gender"
        options={[
          { label: 'آقا', value: 'male' },
          { label: 'خانم', value: 'female' },
        ]}
        renderRadioButton={(Component) => <Box>{Component}</Box>}
        className={css({ gap: '2rem' })}
      />
      <Box>
        <TextInput
          label="نام"
          name="personal_information.first_name"
          placeholder="نام را وارد کنید"
          rules={{ required: requiredText, ...PERSIAN_LATTER_RULE }}
          defaultValue=""
        />
      </Box>
      <Box>
        <TextInput
          label="نام ‌خانوادگی"
          name="personal_information.last_name"
          placeholder="نام ‌خانوادگی را وارد کنید"
          rules={{ required: requiredText, ...PERSIAN_LATTER_RULE }}
          defaultValue=""
        />
      </Box>
      <Box>
        <DateInput
          label="تاریخ تولد"
          name="personal_information.birthdate"
          buttonText="انتخاب تاریخ"
          modalLabel="انتخاب تاریخ تولد"
          defaultValue=""
          rules={{ validate: validateNotFutureDate, required: requiredText }}
        />
      </Box>
      <RadioGroup
        title="تابعیت"
        name="personal_information.driver_iranian"
        options={[
          { label: 'ایرانی', value: 'true' },
          { label: 'غیر ایرانی', value: 'false' },
        ]}
        renderRadioButton={(Component) => <Box>{Component}</Box>}
        className={css({ gap: '2rem' })}
      />
      <Box>
        <TextInput
          label="کدملی"
          name="personal_information.national_code"
          placeholder="کدملی را وارد کنید"
          defaultValue=""
          noCharInput
          rules={{
            required: requiredText,
            ...ONLY_NUMBERS_RULE,
            ...NATIONAL_CODE_RULE,
          }}
          max={10}
        />
      </Box>
      <Box>
        <TextInput
          label="نام پدر"
          name="personal_information.father_name"
          placeholder="نام پدر را وارد کنید"
          rules={{ ...PERSIAN_LATTER_RULE }}
          defaultValue=""
          optionalLabel
        />
      </Box>
      <Box>
        <TextInput
          label="شماره شناسنامه"
          name="personal_information.identity_number"
          placeholder="شماره شناسنامه را وارد کنید"
          rules={{ ...ONLY_NUMBERS_RULE, required: requiredText }}
          max={10}
          defaultValue=""
          noCharInput
        />
      </Box>
      <Box>
        <TextInput
          label="محل صدور شناسنامه"
          name="personal_information.identity_issue_location"
          placeholder="محل صدور شناسنامه را وارد کنید"
          rules={{ ...PERSIAN_LATTER_RULE, required: requiredText }}
          defaultValue=""
        />
      </Box>
      <RadioGroup
        title="آیا راننده مالک وسیله‌نقلیه است ؟"
        name="personal_information.is_owner_driver"
        options={[
          { label: 'بله', value: 'true' },
          { label: 'خیر', value: 'false' },
        ]}
        renderRadioButton={(Component) => <Box>{Component}</Box>}
        className={css({ gap: '2rem' })}
      />
      <LoadingButton
        onClick={methods.handleSubmit(onSubmit)}
        loading={loading}
        fullWidth
        disabled={!methods?.formState?.isValid}
        mt={20}
      >
        ثبت و ادامه
      </LoadingButton>
    </Flex>
  );
};
export default BodyInitialDataDriver;
